// AuthFrame — shared standalone frame for auth/onboarding pages (login,
// workspace creation, invitation accept): Aurora behind, one centered glass
// card in front. Composes existing DS pieces (Aurora + the glass-card tokens,
// per DESIGN_SYSTEM.md §5/§6) — no new tokens or patterns.
import type { CSSProperties, ReactNode } from 'react';
import { Aurora } from '@/design-system/primitives/Aurora';
import { Wordmark as DesignSystemWordmark } from '@/design-system/primitives/Wordmark';

export function AuthFrame({ children, maxWidth = 420 }: { children: ReactNode; maxWidth?: number }) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <Aurora />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth,
          background: 'var(--ds-glass-card-fill)',
          border: '1px solid var(--ds-glass-card-border)',
          backdropFilter: 'var(--ds-glass-blur)',
          WebkitBackdropFilter: 'var(--ds-glass-blur)',
          borderRadius: 'var(--ds-radius-xl)',
          boxShadow: 'var(--ds-shadow-md)',
          padding: '32px 28px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * AuthSplit — the standalone auth/onboarding stage: a display-scale hero set
 * directly on the aurora (the page's one emotional moment, §7.4) beside a
 * compact glass card holding the controls. Wraps to a stack on narrow
 * viewports; entrance is a two-beat rise guarded by prefers-reduced-motion.
 */
export function AuthSplit({ hero, children, cardWidth = 360 }: { hero: ReactNode; children: ReactNode; cardWidth?: number }) {
  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      <Aurora />

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes auth-rise {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: none; }
          }
          .auth-rise-1 { animation: auth-rise 0.55s var(--ds-ease-soft) both; }
          .auth-rise-2 { animation: auth-rise 0.55s var(--ds-ease-soft) 0.1s both; }
        }
      `}</style>

      <header style={{ position: 'absolute', top: 28, left: 28, zIndex: 2 }}>
        <Wordmark />
      </header>

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100vh',
          maxWidth: 1000,
          margin: '0 auto',
          padding: '104px 32px 56px',
          boxSizing: 'border-box',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '48px 72px',
        }}
      >
        <div className="auth-rise-1" style={{ flex: '1 1 300px', minWidth: 280, maxWidth: 420 }}>
          {hero}
        </div>

        <div
          className="auth-rise-2"
          style={{
            flex: `0 1 ${cardWidth}px`,
            minWidth: 300,
            background: 'var(--ds-glass-card-fill)',
            border: '1px solid var(--ds-glass-card-border)',
            backdropFilter: 'var(--ds-glass-blur)',
            WebkitBackdropFilter: 'var(--ds-glass-blur)',
            borderRadius: 'var(--ds-radius-xl)',
            boxShadow: 'var(--ds-shadow-md)',
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** AuthSplit's hero block: display title (brand-purple period appended) + subline(s). */
export function AuthHero({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <>
      <h1
        className="font-display"
        style={{
          fontWeight: 800,
          fontSize: 34,
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          color: 'hsl(var(--ds-text-heading))',
        }}
      >
        {title}
        <span style={{ color: 'hsl(var(--ds-brand-purple-fg))' }}>.</span>
      </h1>
      {children && (
        <div
          className="font-body"
          style={{
            marginTop: 12,
            maxWidth: 360,
            fontSize: 15,
            lineHeight: 1.6,
            color: 'hsl(var(--ds-text-body-color))',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}

/** Small uppercase eyebrow heading the glass card (§2 eyebrow spec). */
export function AuthEyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="font-body"
      style={{
        fontSize: 'var(--ds-text-eyebrow)',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: 'hsl(var(--ds-brand-purple-fg))',
      }}
    >
      {children}
    </p>
  );
}

/** Centered "Loading…" state on the aurora, no card. */
export function AuthLoading({ children = 'Loading…' }: { children?: ReactNode }) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Aurora />
      <p
        className="font-body"
        style={{ position: 'relative', zIndex: 2, fontSize: 'var(--ds-text-body-sm)', color: 'hsl(var(--ds-text-meta))' }}
      >
        {children}
      </p>
    </div>
  );
}

export function AuthHeading({ children }: { children: ReactNode }) {
  return (
    <h1
      className="font-display"
      style={{ fontWeight: 700, fontSize: 'var(--ds-text-title)', letterSpacing: '-0.01em', color: 'hsl(var(--ds-text-heading))' }}
    >
      {children}
    </h1>
  );
}

/** "or" hairline divider between sign-in methods. */
export function AuthDivider({ children = 'or' }: { children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, borderTop: '1px solid var(--ds-divider)' }} />
      <span className="font-body" style={{ fontSize: 'var(--ds-text-label)', color: 'hsl(var(--ds-text-meta))' }}>
        {children}
      </span>
      <div style={{ flex: 1, borderTop: '1px solid var(--ds-divider)' }} />
    </div>
  );
}

/** Quiet inline text action ("Cancel", "Use a different email", "Change"). */
export function TextButton({
  children,
  onClick,
  disabled,
  style,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="font-body ds-focusable"
      style={{
        display: 'block',
        margin: '0 auto',
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        borderRadius: 'var(--ds-radius-xs)',
        fontSize: 'var(--ds-text-label)',
        fontWeight: 500,
        color: 'hsl(var(--ds-text-meta))',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'color var(--ds-dur-fast) var(--ds-ease-soft)',
        ...style,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.color = 'hsl(var(--ds-text-strong))'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'hsl(var(--ds-text-meta))'; }}
    >
      {children}
    </button>
  );
}

/** Form field label (Public Sans, label scale). */
export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label
      className="font-body"
      style={{
        display: 'block',
        fontSize: 'var(--ds-text-label)',
        fontWeight: 600,
        color: 'hsl(var(--ds-text-strong))',
        marginBottom: 6,
      }}
    >
      {children}
    </label>
  );
}

export function Wordmark() {
  return <DesignSystemWordmark className="justify-center" />;
}

/** Google brand glyph, sized to sit inside a DS Button (gap comes from the button). */
export function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}
